import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import type { PermitCreate, AllPermitsQueryOptions } from '@/types/permits';

// ============================================================================
// Query Hooks (Phase 6 Batch 2: Permits)
// ============================================================================

/**
 * Fetches all permits for a specific project
 *
 * @param projectId - The project ID to fetch permits for
 * @returns Query result with permits array
 *
 * @example
 * const { data: permits, isLoading } = usePermits(projectId);
 */
export function usePermits(projectId: string | undefined) {
  return useQuery({
    queryKey: ['permits', projectId],
    queryFn: async () => {
      const response = await api.permits.list(projectId!);
      return response.permits;
    },
    enabled: !!projectId,
  });
}

// ============================================================================
// Phase 9.16b: Standalone Permit List
// ============================================================================

/**
 * Fetches all permits across all projects with optional filters
 *
 * @param options - Query options for filtering, pagination, sorting
 * @returns Query result with permits array, total count, and pagination info
 *
 * Uses `keepPreviousData` for smooth transitions when filters change.
 *
 * @example
 * const { data, isLoading } = useAllPermits({
 *   search: 'building',
 *   status: 'submitted',
 *   sortBy: 'due_date',
 *   sortOrder: 'asc',
 *   limit: 50,
 *   offset: 0,
 * });
 */
export function useAllPermits(options: AllPermitsQueryOptions = {}) {
  return useQuery({
    queryKey: ['permits', 'all', options],
    queryFn: async () => {
      const response = await api.permits.listAll(options);
      return response;
    },
    placeholderData: (previousData) => previousData, // Keep previous data while loading new
  });
}

// ============================================================================
// Mutation Hooks (Phase 6 Batch 2: Permit CRUD operations)
// ============================================================================

/**
 * Creates a new permit
 *
 * Uses cache invalidation (not optimistic) because backend calculates status
 * and may have validation rules.
 *
 * Cache invalidation: Uses prefix matching `['permits']` to invalidate ALL
 * permit queries (by ID, by project, etc.) following REACT_QUERY_PATTERNS.md
 *
 * @example
 * const createPermit = useCreatePermit();
 * await createPermit.mutateAsync({
 *   projectId: 'proj-123',
 *   permit: { permit_type: 'Building Permit', ... }
 * });
 */
export function useCreatePermit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { projectId: string; permit: PermitCreate }) =>
      api.permits.create(data.projectId, data.permit),
    onSuccess: (response, variables) => {
      // ✅ Prefix matching - invalidates ALL permit queries
      // Follows REACT_QUERY_PATTERNS.md (lines 144-215)
      queryClient.invalidateQueries({ queryKey: ['permits'] });
      toast.success('Permit created successfully');
      return response;
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create permit');
    },
  });
}

/**
 * Updates an existing permit
 *
 * Uses cache invalidation (not optimistic) to ensure UI shows
 * server-calculated status and validation results.
 *
 * @example
 * const updatePermit = useUpdatePermit();
 * await updatePermit.mutateAsync({
 *   id: 'permit-123',
 *   data: { fees_costs: 3000 }
 * });
 */
export function useUpdatePermit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PermitCreate }) =>
      api.permits.update(id, data),
    onSuccess: (response, variables) => {
      // ✅ Prefix matching - invalidates all permit queries
      queryClient.invalidateQueries({ queryKey: ['permits'] });
      toast.success('Permit updated successfully');
      return response;
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update permit');
    },
  });
}

/**
 * Deletes a permit with optimistic updates
 *
 * Pattern: Optimistic update (onMutate) + Server sync (onSuccess)
 * Avoids race condition by only invalidating AFTER server confirms deletion
 *
 * Benefits:
 * - ✅ Instant UI update (permit removed immediately)
 * - ✅ Automatic rollback on error
 * - ✅ No race conditions (invalidate after success, not in onSettled)
 * - ✅ Server sync on success
 *
 * @example
 * const deletePermit = useDeletePermit();
 * await deletePermit.mutateAsync({ permitId: 'permit-123', projectId: 'proj-456' });
 */
export function useDeletePermit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (permitId: string) => api.permits.delete(permitId),

    onSuccess: () => {
      // Use simple invalidation pattern (like billable items - proven to work)
      // Invalidates all permit queries, data refetches when component remounts
      queryClient.invalidateQueries({ queryKey: ['permits'] });
      toast.success('Permit deleted successfully');
    },

    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete permit');
    },
  });
}

// ============================================================================
// Phase 8.2: Permit Renewal Hook
// ============================================================================

/**
 * Renews a recurring permit
 *
 * Creates a new permit with the next due date based on the recurring_period.
 * The old permit is marked as renewed (renewed_by_permit_id set).
 *
 * @example
 * const renewPermit = useRenewPermit();
 * await renewPermit.mutateAsync({
 *   projectId: 'proj-123',
 *   permitId: 'permit-456'
 * });
 */
export function useRenewPermit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { projectId: string; permitId: string }) =>
      api.permits.renew(data.projectId, data.permitId),
    onSuccess: (response) => {
      // Invalidate all permit queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['permits'] });
      toast.success('Permit renewed successfully');
      return response;
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to renew permit');
    },
  });
}

// ============================================================================
// Phase 9.16a: Permit Type Definitions
// ============================================================================

/**
 * Fetches permit type definitions for the current tenant
 *
 * Used to populate permit type dropdowns and get field_schema for dynamic forms.
 *
 * @param options - Query options (includeInactive, category)
 * @returns Query result with permit type definitions array
 *
 * @example
 * const { data } = usePermitTypes();
 * const types = data?.permit_types || [];
 */
export function usePermitTypes(options: { includeInactive?: boolean; category?: string } = {}) {
  return useQuery({
    queryKey: ['permit-types', options],
    queryFn: async () => {
      const response = await api.permitTypes.list(options);
      return response;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - permit types rarely change
  });
}
